<template>
  <div class="api-doc-container">
    <!-- 基本信息 -->
    <a-divider orientation="left">基本信息</a-divider>
    <a-descriptions :column="2" bordered>
      <a-descriptions-item label="请求方法">
        <a-tag :color="config.method === 'POST' ? 'blue' : 'green'">
          {{ config.method }}
        </a-tag>
      </a-descriptions-item>
      <a-descriptions-item label="请求路径">
        <code>{{ config.path }}</code>
      </a-descriptions-item>
      <a-descriptions-item label="接口描述" :span="2">
        {{ config.description }}
      </a-descriptions-item>
    </a-descriptions>

    <!-- 请求参数 -->
    <a-divider orientation="left" v-if="config.requestParams || config.queryParams">
      {{ config.method === 'GET' ? '查询参数' : '请求参数' }}
    </a-divider>
    <a-table
      v-if="config.requestParams || config.queryParams"
      :columns="paramColumns"
      :data-source="config.requestParams || config.queryParams"
      :pagination="false"
      size="small"
      bordered
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'required'">
          <a-tag :color="record.required ? 'red' : 'default'">
            {{ record.required ? '必需' : '可选' }}
          </a-tag>
        </template>
        <template v-else-if="column.key === 'type'">
          <code>{{ record.type }}</code>
        </template>
      </template>
    </a-table>

    <!-- 请求示例 -->
    <a-divider orientation="left">请求示例</a-divider>
    <div v-if="config.method === 'POST' && config.requestExample">
      <a-radio-group v-model:value="selectedExample" @change="handleExampleChange">
        <a-radio-button value="create">创建计划</a-radio-button>
        <a-radio-button value="continue">继续执行</a-radio-button>
      </a-radio-group>
      <div style="margin-top: 16px">
        <CodeEditor
          :model-value="currentExampleJson"
          language="json"
          :height="300"
          :read-only="true"
        />
      </div>
    </div>
    <div v-else-if="config.method === 'GET' && config.requestExample">
      <a-alert
        :message="config.requestExample.url"
        type="info"
        show-icon
        style="margin-bottom: 16px"
      />
    </div>

    <!-- 响应示例 -->
    <a-divider orientation="left">响应示例</a-divider>
    <a-tabs v-if="config.method === 'POST' && config.responseExample">
      <a-tab-pane key="success" tab="成功响应（创建）">
        <CodeEditor
          :model-value="formatJson(config.responseExample.success)"
          language="json"
          :height="200"
          :read-only="true"
        />
      </a-tab-pane>
      <a-tab-pane key="continue" tab="成功响应（继续）">
        <CodeEditor
          :model-value="formatJson(config.responseExample.continue)"
          language="json"
          :height="200"
          :read-only="true"
        />
      </a-tab-pane>
      <a-tab-pane key="completed" tab="完成响应">
        <CodeEditor
          :model-value="formatJson(config.responseExample.completed)"
          language="json"
          :height="200"
          :read-only="true"
        />
      </a-tab-pane>
    </a-tabs>
    <div v-else-if="config.method === 'GET' && config.responseExample">
      <a-tabs>
        <a-tab-pane key="executing" tab="执行中">
          <CodeEditor
            :model-value="formatJson(config.responseExample.executing)"
            language="json"
            :height="300"
            :read-only="true"
          />
        </a-tab-pane>
        <a-tab-pane key="completed" tab="已完成">
          <CodeEditor
            :model-value="formatJson(config.responseExample.completed)"
            language="json"
            :height="300"
            :read-only="true"
          />
        </a-tab-pane>
      </a-tabs>
    </div>

    <!-- 错误码说明 -->
    <a-divider orientation="left">错误码说明</a-divider>
    <a-table
      :columns="errorColumns"
      :data-source="config.errorCodes || []"
      :pagination="false"
      size="small"
      bordered
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'code'">
          <a-tag color="red">{{ record.code }}</a-tag>
        </template>
        <template v-else-if="column.key === 'examples'">
          <ul style="margin: 0; padding-left: 20px">
            <li v-for="(example, index) in record.examples" :key="index">
              <code>{{ example }}</code>
            </li>
          </ul>
        </template>
      </template>
    </a-table>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import CodeEditor from './CodeEditor.vue';

export default {
  name: 'ApiDoc',
  components: {
    CodeEditor
  },
  props: {
    config: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const selectedExample = ref('create');

    const paramColumns = [
      { title: '参数名', dataIndex: 'name', key: 'name', width: 150 },
      { title: '类型', key: 'type', width: 100 },
      { title: '是否必需', key: 'required', width: 100 },
      { title: '说明', dataIndex: 'description', key: 'description' },
      { title: '示例', dataIndex: 'example', key: 'example', width: 200 }
    ];

    const errorColumns = [
      { title: '错误码', key: 'code', width: 100 },
      { title: '错误信息', dataIndex: 'message', key: 'message', width: 150 },
      { title: '说明', dataIndex: 'description', key: 'description' },
      { title: '示例', key: 'examples' }
    ];

    const currentExampleJson = computed(() => {
      if (props.config.method === 'POST' && props.config.requestExample) {
        const example = props.config.requestExample[selectedExample.value];
        return formatJson(example?.data || {});
      }
      return '';
    });

    const handleExampleChange = () => {
      // 示例切换时自动更新
    };

    const formatJson = (obj) => {
      if (!obj) return '';
      try {
        return JSON.stringify(obj, null, 2);
      } catch (e) {
        return String(obj);
      }
    };

    return {
      selectedExample,
      paramColumns,
      errorColumns,
      currentExampleJson,
      handleExampleChange,
      formatJson
    };
  }
};
</script>

<style scoped>
.api-doc-container {
  padding: 16px 0;
}
</style>

